import { useState, useEffect } from 'react'
import { fetchInsightCells } from '../helpers/sectorInsights/fetchInsightCells'
import buildAgendaRows from '../helpers/intelligence/buildAgendaRows'

/**
 * De Agenda: welke inzicht-cellen er zijn en hoeveel er nog aan ontbreekt tot
 * ze publiceerbaar zijn, dichtstbij eerst.
 *
 * `loaded` en `isEmpty` zijn eigen toestanden, geen afgeleide van `rows.length`.
 * Een lege agenda is een BEWERING aan de lezer — "er is niets te doen" — en die
 * is onwaar zolang de gegevens onderweg zijn of de read geweigerd werd. De
 * volgorde in de UI hoort dus te zijn: eerst 'weet ik niet', dan 'leeg', dan de
 * inhoud.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * `version` is de ISS-versie waaronder de SERVER gerekend heeft. Die hoort
 * zichtbaar te zijn: leest een gedeployde functie een ander versienummer dan de
 * nachtelijke berekening schrijft, dan komt er stil een lege lijst terug — niet
 * te onderscheiden van "nog niets berekend". Zo bleef dat vijf dagen onopgemerkt.
 *
 * `herberekeningNodig` is een DERDE toestand naast leeg en gevuld: er staan wél
 * cellen, maar onder een oudere ISS-versie. Dan is er net een versiebump
 * geweest en moet de nachtelijke run nog draaien — "er is niets te doen" zou
 * dan onwaar zijn. `null` = nog niet bekend; die mag nooit als `false` lezen.
 *
 * @param {string|null} [msiId] - null = alle cellen (de lijst); een effect-id = alleen die cellen
 *   (het themadetail heeft er maar één nodig — de volledige lees kost seconden); '' = nog niet
 *   bekend, dan geen aanroep
 * @returns {{rows: Array<object>, p2Targets: (object|null), loading: boolean, loaded: boolean, isEmpty: boolean, version: (string|null), herberekeningNodig: (boolean|null), error: (Error|null)}}
 */
const useAgenda = (msiId = null) => {
    const [rows, setRows] = useState(/** @type {Array<object>} */([]))
    // De P2-drempels van de server. Ook doorgegeven aan de onderbouwingstabel
    // onder een rij: die rekende met een eigen kopie, en dan staan er twee
    // getallen uit twee bronnen op hetzelfde scherm.
    const [p2Targets, setP2Targets] = useState(/** @type {object|null} */(null))
    const [version, setVersion] = useState(/** @type {string|null} */(null))
    // null = nog niet bekend, en dat is iets anders dan 'nee'.
    const [herberekeningNodig, setHerberekeningNodig] = useState(/** @type {boolean|null} */(null))
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(/** @type {Error|null} */(null))

    useEffect(() => {
        if (msiId === '') return undefined
        let cancelled = false
        setLoaded(false)
        setError(null)

        fetchInsightCells(msiId ? { msiId } : {})
            .then((result) => {
                if (cancelled) return
                setRows(buildAgendaRows(result?.cells, result?.p2Targets))
                setP2Targets(result?.p2Targets || null)
                setVersion(typeof result?.version === 'string' ? result.version : null)
                setHerberekeningNodig(result?.herberekeningNodig === true)
                setLoaded(true)
            })
            .catch((err) => {
                if (cancelled) return
                // Laten zien, niet wegslikken: een geweigerde read mag nooit
                // als 'geen werk op de plank' op het scherm belanden.
                console.error('useAgenda:', err)
                setError(err)
                setRows([])
                setP2Targets(null)
                setVersion(null)
                setHerberekeningNodig(null)
                setLoaded(false)
            })

        return () => { cancelled = true }
    }, [msiId])

    return {
        rows,
        p2Targets,
        herberekeningNodig,
        loading: !loaded && error === null,
        loaded,
        // Alleen waar als we het ZEKER weten.
        isEmpty: loaded && rows.length === 0,
        version,
        error,
    }
}

export default useAgenda
